import { type ErrorComponentProps, isNotFound, type NotFoundRouteProps } from '@tanstack/react-router';
import { type ComponentType, type ReactNode, useCallback } from 'react';
// oxlint-disable-next-line no-restricted-imports
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { DefaultErrorFallbackBasic } from './DefaultErrorFallback';
import { DefaultNotFoundFallbackBasic } from './DefaultNotFoundFallback';

const PassThroughWrap = ({ children }: { children: ReactNode }) => <>{children}</>;

export function ErrorBoundary({
  children,
  InnerWrap = PassThroughWrap,
  ErrorComponent = DefaultErrorFallbackBasic,
  NotFoundComponent = DefaultNotFoundFallbackBasic,
}: {
  children: ReactNode;
  InnerWrap?: ComponentType<{ children: ReactNode }>;
  ErrorComponent?: ComponentType<ErrorComponentProps>;
  NotFoundComponent?: ComponentType<NotFoundRouteProps>;
}) {
  const fallbackRender = useCallback(
    ({ error, resetErrorBoundary }: { error: unknown; resetErrorBoundary: () => void }) => {
      if (isNotFound(error)) {
        return (
          <InnerWrap>
            <NotFoundComponent {...(error as NotFoundRouteProps)} />
          </InnerWrap>
        );
      }
      return (
        <InnerWrap>
          <ErrorComponent error={error as Error} reset={resetErrorBoundary} />
        </InnerWrap>
      );
    },
    [ErrorComponent, NotFoundComponent, InnerWrap],
  );

  return <ReactErrorBoundary fallbackRender={fallbackRender}>{children}</ReactErrorBoundary>;
}

export function NullErrorBoundary({ children }: { children: ReactNode }) {
  return <ReactErrorBoundary fallback={null}>{children}</ReactErrorBoundary>;
}
